import Vue from "vue";
import { validateAndFormatBeforeEdit } from "../../common/validateAndFormatBeforeEdit";
import { nameMixin } from "../../../mixins/nameMixin";

/**
 * Unshift the given data to the given list
 *
 * @param {Array} list
 * @param {Object} value
 * @param {String} type
 * @param {Object} state
 * @param {String} setName
 * @param {Number} providerId
 */
function unshiftToList({ list, value, type, state, setName, providerId }) {
  // Step 5: New list by inserting at the beginging
  const newList = [value, ...list];

  // Step 6: Setting the state
  Vue.set(state.appointmentType, setName, {
    ...state.appointmentType.clients,
    [providerId]: newList
  });

  // Step 7: Remove this on refreh
  Vue.set(state.appointmentType, "removeOnRefresh", {
    id: value.id,
    type,
    providerId
  });
}

/**
 * Function to check weather the appointment we are trying to edit is an exclusive appointment,
 * then we need to add the appointment to appointment type
 *
 * @param {Number} id
 * @param {String} type
 * @param {Object} appointment
 * @param {State} state
 */
function checkForOnlyThisAppointmentEdit(id, type, appointment, state) {
  // Step 1: Get the provider id
  const providerId = isNaN(appointment["provider_id"])
    ? false
    : parseInt(appointment["provider_id"]);

  // Step 3: Differentiate between type
  if (type === "person") {
    // Step 3.1: If selected index is -1 that means that the index for that appointment has not been found and needs to be added
    if (id !== 0) {
      return id;
    }

    // Step 4: Formatting name as per calendar settings
    const clientTitle = nameMixin.methods.formatName(
      appointment.firstName,
      appointment.middleName,
      appointment.lastName,
      appointment.namePrefix,
      appointment.nameSuffix
    );

    // Step 5: New list by inserting at the beginging
    unshiftToList({
      list: state.appointmentType.clients[providerId],
      value: {
        id: appointment["client_id"],
        name: clientTitle,
        type: "person"
      },
      type,
      state,
      setName: "clients",
      providerId
    });
  } else {
    // Step 8: Get the title
    const title = appointment["otherevent_name"];

    // Step 9 : Differentiate between other type
    if (type === "other_event") {
      // Step 3.1: If selected index is -1 that means that the index for that appointment has not been found and needs to be added
      if (
        state.appointmentType.people[providerId].find(person => {
          return person.id === id;
        })
      ) {
        return id;
      }

      // Step 10: New list by inserting at the beginging
      unshiftToList({
        list: state.appointmentType.people[providerId],
        value: {
          id: appointment["otherevent_id"],
          type: "other_event",
          name: title
        },
        type,
        state,
        setName: "people",
        providerId
      });
    } else if (type === "event_appointment") {
      // Step 3.1: If selected index is -1 that means that the index for that appointment has not been found and needs to be added
      if (id !== 0) {
        return id;
      }

      // Step 10: New list by inserting at the beginging
      unshiftToList({
        list: state.appointmentType.events[providerId],
        value: {
          id: appointment["otherevent_id"],
          type: "event_appointment",
          name: appointment["app_oth_event"]
        },
        type,
        state,
        setName: "events",
        providerId
      });
    } else if (type === "group_appointment") {
      // Step 3.1: If selected index is -1 that means that the index for that appointment has not been found and needs to be added
      if (id !== 0) {
        return id;
      }

      // Step 10: New list by inserting at the beginging
      unshiftToList({
        list: state.appointmentType.groups[providerId],
        value: {
          id: appointment["otherevent_id"],
          type: "group_appointment",
          name: title
        },
        type,
        state,
        setName: "groups",
        providerId
      });
    }
  }

  // Step 13: Return the id of the new element
  return 0;
}

/**
 * Restore all the elements,
 * that can be restored to default without any conditions, to default
 *
 * @param {Object} state
 */
function removeOnRefreshEventAction(state) {
  // Step 1: Get the provider id
  const providerId = state.appointmentType.removeOnRefresh.providerId;

  // Step 2: New list by removing id
  let newEvents = state.appointmentType.events[providerId].filter(curEvent => {
    return curEvent.id !== state.appointmentType.removeOnRefresh.id;
  });

  // Step 3: Setting the state
  Vue.set(state.appointmentType, "events", {
    ...state.appointmentType.events,
    [providerId]: newEvents
  });
}

/**
 * Restoring the state to default
 *
 * @param {Object} state
 */
function restoreDefaultForAddAppointment(state) {
  // Step 1: Get the provider id
  const providerId = state.appointmentType.removeOnRefresh.providerId;

  // Step 2: Remove the index from appointmentType if required
  if (state.appointmentType.removeOnRefresh.index !== -1 && providerId !== 0) {
    // Step 3: Differentiate between type
    if (state.appointmentType.removeOnRefresh.type === "person") {
      // Step 4: New list by removing index
      let newClients = state.appointmentType.clients[providerId].filter(
        curClient => curClient.id !== state.appointmentType.removeOnRefresh.id
      );

      // Step 5: Setting the state
      Vue.set(state.appointmentType, "clients", {
        ...state.appointmentType.clients,
        [providerId]: newClients
      });
    } else if (state.appointmentType.removeOnRefresh.type === "other_event") {
      // Step 5: New list by removing index
      let newPeople = state.appointmentType.people[providerId].filter(
        curPerson => curPerson.id !== state.appointmentType.removeOnRefresh.id
      );

      // Step 6: Setting the state
      Vue.set(state.appointmentType, "people", {
        ...state.appointmentType.people,
        [providerId]: newPeople
      });
    } else if (
      state.appointmentType.removeOnRefresh.type === "event_appointment"
    ) {
      removeOnRefreshEventAction(state);
    } else if (
      state.appointmentType.removeOnRefresh.type === "group_appointment"
    ) {
      // Step 5: New list by removing index
      let newGroups = state.appointmentType.groups[providerId].fiter(
        curGroup => curGroup.id !== state.appointmentType.removeOnRefresh.id
      );

      // Step 6: Setting the state
      Vue.set(state.appointmentType, "groups", {
        ...state.appointmentType.groups,
        [providerId]: newGroups
      });
    }
  }

  // Step 7: Set appointment type defaults
  Vue.set(state.appointmentType, "removeOnRefresh", {
    type: "",
    id: -1,
    providerId: 0
  });

  if (state.appointmentType.clear) {
    Vue.set(state.appointmentType, "selectedId", 0);
    Vue.set(state.appointmentType, "selectedType", "");
    state.edit = {
      isEdit: false,
      editMode: false,
      editLevel: 0,
      firstClick: false,
      id: 0,
      oneAllFuture: "single",
      type: "",
      selectedIndex: -1,
      otherType: "",
      usePsybooksVideo: false,
      useConnectVideo: false
    };
  }

  Vue.set(state.appointmentType, "clear", true);

  // Step 8: Set use psybooks video as false by default
  state.usePsybooksVideo = false;

  // Step 8: Set use psybooks connect video as false by default
  state.useConnectVideo = false;

  // Step 9: Set video reminder as none
  state.videoReminder = "N";

  // Step 10: Simple defaults
  state.loadingAddAppointment = false;
  state.overLapCheck = "NOT_CHECKED";

  // Step 11: Setting defaults
  state.allDay = false;
  state.date = {
    dayOfMonth: 0,
    month: 0,
    year: 0
  };
  state.startTime = {
    hr: 10,
    min: 0,
    sec: 0
  };
  state.repeat = {
    order: [
      { innerOrOuter: "outer", index: 0 },
      { innerOrOuter: "inner", index: 0 },
      { innerOrOuter: "outer", index: 1 },
      { innerOrOuter: "outer", index: 2 },
      { innerOrOuter: "outer", index: 3 },
      { innerOrOuter: "inner", index: 1 },
      { innerOrOuter: "outer", index: 4 },
      { innerOrOuter: "inner", index: 2 },
      { innerOrOuter: "inner", index: 3 }
    ],
    innerOptions: ["Daily", "Weekly", "Monthly", "Yearly"],
    outerOptions: [
      "Never",
      "Every weekday (Mon-Fri)",
      "Every Mon, Wed and Fri",
      "Every Tue and Thu",
      "Every other week"
    ],
    innerOrOuter: "outer",
    selectedIndex: 0,
    custom: 1,
    weeklyOn: {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false
    },
    fullMonthlyType: ""
  };
  state.endRepeat = {
    dayOfMonth: 0,
    month: 0,
    year: 0,
    never: true
  };
  state.startRepeat = {
    dayOfMonth: 0,
    month: 0,
    year: 0
  };
  state.isOffice = true;
  state.isVideo = false;
  state.isPhone = false;
  state.isOther = false;
  state.notes = "";
  state.secondLevelHeaderActive = false;
  state.hideBackAndAddButton = false;
}

/**
 * Mutations for add appointment
 */
export const mutations = {
  /**
   * Set the over lap check value
   *
   * @param {Object} state
   * @param {State} value
   */
  setOverLapCheck(state, value) {
    state.overLapCheck = value;
  },
  /**
   * Setting the loading of add appointment
   *
   * @param {Object} state
   * @param {Boolean} value
   */
  setLoadingAddAppointment(state, value) {
    state.loadingAddAppointment = value;
  },
  /**
   * Setting the notes
   *
   * @param {Object} state
   * @param {String} notes
   */
  setNotes(state, notes) {
    state.notes = notes;
  },
  /**
   * Toggle all day
   *
   * @param {Object} state
   */
  toggleAllDay(state) {
    state.allDay = !state.allDay;
  },
  /**
   * Set is video
   *
   * @param {Object} state
   */
  setIsVideo(state) {
    state.isVideo = true;
    state.isOffice = false;
    state.isPhone = false;
    state.isOther = false;
  },
  /**
   * Set the edit level
   *
   * @param {Object} state
   * @param {Number} level
   */
  setEditLevel(state, level) {
    Vue.set(state.edit, "editLevel", isNaN(level) ? 0 : parseInt(level));
  },
  /**
   * Set the edit mode
   *
   * @param {Object} state
   * @param {Boolean} mode
   */
  setEditMode(state, mode) {
    Vue.set(state.edit, "editMode", mode === true);
  },
  /**
   * Set weather second level of header is active or not
   *
   * @param {*} state
   * @param {*} value
   */
  setSecondLevelHeaderActive(state, value) {
    state.secondLevelHeaderActive = value;
  },
  /**
   * Set the hide and back button
   *
   * @param {*} state
   * @param {*} value
   */
  setHideBackAndAddButton(state, value) {
    state.hideBackAndAddButton = value;
  },
  /**
   * Set weather the first click is completed
   *
   * @param {Object} state
   * @param {Boolean} click
   */
  setFirstClick(state, click) {
    Vue.set(state.edit, "firstClick", click === true);
  },
  /**
   * Set is office
   *
   * @param {Object} state
   */
  setIsOffice(state) {
    state.isVideo = false;
    state.isOffice = true;
    state.isPhone = false;
    state.isOther = false;
  },
  /**
   * Set is phone
   *
   * @param {Object} state
   */
  setIsPhone(state) {
    state.isVideo = false;
    state.isOffice = false;
    state.isPhone = true;
    state.isOther = false;
  },
  /**
   * Set is Other
   *
   * @param {*} state
   */
  setIsOther(state) {
    state.isVideo = false;
    state.isOffice = false;
    state.isPhone = false;
    state.isOther = true;
  },
  /**
   * Set weather to use psybooks video or not
   *
   * @param {Object} state
   * @param {Boolean} value
   */
  setUsePsybooksVideo(state, value) {
    value = value === true;
    state.usePsybooksVideo = value;
  },

  /**
   * Set weather to use psybooks connect video or not
   *
   * @param {Object} state
   * @param {Boolean} value
   */
  setUseConnectVideo(state, value) {
    value = value === true;
    state.useConnectVideo = value;
  },

  /**
   * Set the date
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setDate(state, { dayOfMonth, month, year }) {
    Vue.set(state.date, "dayOfMonth", dayOfMonth);
    Vue.set(state.date, "month", month);
    Vue.set(state.date, "year", year);
  },
  /**
   * Setting the end time of the appointment
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setEndTime(state, { hr, min, sec }) {
    Vue.set(state.endTime, "hr", hr);
    Vue.set(state.endTime, "min", min);
    Vue.set(state.endTime, "sec", sec);
  },
  /**
   * Set when to end repeat
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setEndRepeat(state, { dayOfMonth, month, year, never }) {
    Vue.set(state.endRepeat, "dayOfMonth", dayOfMonth);
    Vue.set(state.endRepeat, "month", month);
    Vue.set(state.endRepeat, "year", year);
    Vue.set(state.endRepeat, "never", never);
  },

  /**
   * Set when to start the repeat
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setStartRepeat(state, { dayOfMonth, month, year }) {
    Vue.set(state.startRepeat, "dayOfMonth", dayOfMonth);
    Vue.set(state.startRepeat, "month", month);
    Vue.set(state.startRepeat, "year", year);
  },
  /**
   * Set the office location
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setOfficeLocation(state, { custom, customValue, selectedIndex }) {
    Vue.set(state.officeLocation, "custom", custom);
    Vue.set(state.officeLocation, "customValue", customValue);
    Vue.set(state.officeLocation, "selectedIndex", selectedIndex);
  },
  /**
   * Set the video reminder
   *
   * @param {Object} state
   * @param {String} value
   */
  setVideoReminder(state, value) {
    state.videoReminder = value;
  },
  /**
   * Set the locations
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setLocations(state, { locationDetails }) {
    Vue.set(state.officeLocation, "locationDetails", locationDetails);
  },
  /**
   * Set the appointment type
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setAppointmentType(state, { selectedType, selectedId }) {
    Vue.set(state.appointmentType, "selectedType", selectedType);
    Vue.set(state.appointmentType, "selectedId", selectedId);
  },
  /**
   * Set all the appointment types
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setTypesAppointmentTypes(
    state,
    {
      otherEvent,
      groupAppointment,
      eventAppointment,
      person,
      appUsers,
      subscribers
    }
  ) {
    Vue.set(state.appointmentType, "clients", person);
    Vue.set(state.appointmentType, "people", otherEvent);
    Vue.set(state.appointmentType, "events", eventAppointment);
    Vue.set(state.appointmentType, "groups", groupAppointment);
    Vue.set(state.appointmentType, "appUsers", appUsers);
    Vue.set(state.appointmentType, "subscribers", subscribers);
    Vue.set(state.appointmentType, "fetched", true);
  },
  /**
   * Set the repeat
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setRepeat(state, { selectedIndex, innerOrOuter }) {
    Vue.set(state.repeat, "selectedIndex", selectedIndex);
    Vue.set(state.repeat, "innerOrOuter", innerOrOuter);
  },
  /**
   * Set on which of the week to repeat weekly repeat
   *
   * @param {Object} state
   * @param {Object} index
   */
  setRepeatWeeklyOn(state, { index, value }) {
    Vue.set(state.repeat, "weeklyOn", {
      ...state.repeat.weeklyOn,
      [index]: value
    });
  },
  /**
   * Set the full type of the month
   *
   * @param {Object} state
   * @param {String} value
   */
  setFullMonthlyType(state, value) {
    Vue.set(state.repeat, "fullMonthlyType", value);
  },
  /**
   * Set the repeat custom value
   *
   * @param {Object} state
   * @param {Number} custom
   */
  setRepeatCustom(state, custom) {
    Vue.set(state.repeat, "custom", custom);
  },
  /**
   * Set weather the form has been submitted or not
   *
   * @param {Object} state
   * @param {Boolean} value
   */
  setSubmitted(state, value) {
    state.appointmentSent = value;
  },
  setEditSingleFutureAll(state, value) {
    Vue.set(state.edit, "oneAllFuture", value);
  },
  setAppointmentTypeClear(state, value) {
    Vue.set(state.appointmentType, "clear", value);
  },

  /**
   * Set the start time of the appointment
   * When start time is set, we also need to set the end time according to the increment time set by the user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setStartTime(state, { hr, min, sec }) {
    // Setting start time from what user set
    Vue.set(state.startTime, "hr", hr);
    Vue.set(state.startTime, "min", min);
    Vue.set(state.startTime, "sec", sec);

    // Setting end time from calendar settings
    Vue.set(
      state.endTime,
      "hr",
      hr +
        state.endTime.incrementBy.hr +
        parseInt((min + state.endTime.incrementBy.min) / 60) >
        23
        ? 23
        : hr +
            state.endTime.incrementBy.hr +
            parseInt((min + state.endTime.incrementBy.min) / 60)
    );
    Vue.set(state.endTime, "min", (min + state.endTime.incrementBy.min) % 60);
    Vue.set(state.endTime, "sec", sec + state.endTime.incrementBy.sec);
  },

  /**
   * Add a client to the list of clients in the clients
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addToClientListInForm(state, { client, providerId }) {
    if (providerId in state.appointmentType.clients) {
      const distinctIDs = state.appointmentType.clients[providerId].map(
        localClient => {
          return localClient.id;
        }
      );

      if (distinctIDs.indexOf(client.id) === -1) {
        // Insert at the start of the list
        let clients = {
          ...state.appointmentType.clients,
          [providerId]: [
            {
              id: client.id,
              name: client.lastName + ", " + client.firstName,
              type: "person",
              provider: providerId,
              phone: client.phone,
              favourite_id: client.favourite_id
            },
            ...state.appointmentType.clients[providerId]
          ]
        };

        // Sorting the list
        clients[providerId] = clients[providerId].sort((x, y) => {
          return x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1;
        });

        // Setting selected index
        Vue.set(state.appointmentType, "selectedId", client.id);
        Vue.set(state.appointmentType, "selectedType", "person");

        Vue.set(state.appointmentType, "clients", clients);
      }
    } else if (state.appointmentType.fetched) {
      // Add client to the list for only this,
      //  even if not fetched because it won't be fetched
      const clients = {
        ...state.appointmentType.clients,
        [providerId]: [
          {
            id: client.id,
            name: client.lastName + ", " + client.firstName,
            type: "person",
            provider: providerId,
            phone: client.phone,
            favourite_id: client.favourite_id
          }
        ]
      };

      Vue.set(state.appointmentType, "clients", clients);
    }
  },

  /**
   * Add a person to the list of person for the form
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addToPersonListInForm(state, { person, providerId, onlyThis }) {
    // Step 1: Initialize new people and people list to false
    let people = false;

    // Step 2: Check if provider id is in the object, so has been fetched
    if (providerId in state.appointmentType.people) {
      // Step 3: Get all the distinct people id
      const distinctIDs = state.appointmentType.people[providerId].map(
        localPerson => {
          return localPerson.id;
        }
      );

      // Step 4: Check if the current person's id is in it
      if (distinctIDs.indexOf(person.id) === -1) {
        // Insert at the start of the list, if not inside list
        people = {
          ...state.appointmentType.people,
          [providerId]: [
            {
              id: person.id,
              name: person.lastName + ", " + person.firstName,
              first_name: person.firstName,
              last_name: person.lastName,
              type: "other_event",
              provider: providerId,
              phone: person.phones,
              pht: [person.pht1, person.pht2, person.pht3, person.pht4],
              email: [person.email, person.email2],
              favourite_id: person.favourite_id,
              user_id: 0,
              timezone: person.timezone,
              status: person.status
            },
            ...state.appointmentType.people[providerId]
          ]
        };

        // Sorting the list
        people[providerId] = people[providerId].sort((x, y) => {
          return x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1;
        });

        // Setting selected id and type
        Vue.set(state.appointmentType, "selectedId", person.id);
        Vue.set(state.appointmentType, "selectedType", "other_event");
      }
    } else if (state.appointmentType.fetched || onlyThis) {
      // Add people to the list for only this,
      //  even if not fetched because it won't be fetched
      people = {
        ...state.appointmentType.people,
        [providerId]: [
          {
            id: person.id,
            name: person.lastName + ", " + person.firstName,
            first_name: person.firstName,
            last_name: person.lastName,
            type: "other_event",
            provider: providerId,
            phone: person.phones,
            pht: [person.pht1, person.pht2, person.pht3, person.pht4],
            email: [person.email, person.email2],
            favourite_id: person.favourite_id,
            user_id: 0,
            timezone: person.timezone,
            status: person.status
          }
        ]
      };
    }

    // Adding to the state
    if (people) {
      Vue.set(state.appointmentType, "people", people);
      if (onlyThis) {
        // Remove this on refreh
        Vue.set(state.appointmentType, "removeOnRefresh", {
          type: "other_event",
          id: person.id,
          providerId
        });
      }
    }
  },

  /**
   * Adding event to the list of events for the form
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addToEventListInForm(state, { event, providerId, onlyThis }) {
    let events = false;
    if (providerId in state.appointmentType.events) {
      const distinctIDs = state.appointmentType.events[providerId].map(
        localEvent => {
          return localEvent.id;
        }
      );

      if (distinctIDs.indexOf(event.id) === -1 || onlyThis) {
        // Insert at the start of the list
        events = {
          ...state.appointmentType.events,
          [providerId]: [
            {
              id: event.id,
              name: event.name,
              type: "event_appointment",
              provider: providerId
            },
            ...state.appointmentType.events[providerId]
          ]
        };

        // Set the new selected
        Vue.set(state.appointmentType, "selectedId", event.id);
        Vue.set(state.appointmentType, "selectedType", "event_appointment");
      }
    } else if (state.appointmentType.fetched || onlyThis) {
      events = {
        ...state.appointmentType.events,
        [providerId]: [
          {
            id: event.id,
            name: event.name,
            type: "event_appointment",
            provider: providerId
          }
        ]
      };
    }

    if (events) {
      Vue.set(state.appointmentType, "events", events);

      if (onlyThis) {
        // Remove this on refreh
        Vue.set(state.appointmentType, "removeOnRefresh", {
          type: "event_appointment",
          id: event.id,
          providerId
        });
      }
    }
  },

  /**
   * Adding group to the list of groups for the form
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addToGroupListInForm(state, { group, providerId }) {
    let groups = false;
    if (providerId in state.appointmentType.groups) {
      const distinctIDs = state.appointmentType.groups[providerId].map(
        localGroup => {
          return localGroup.id;
        }
      );

      if (distinctIDs.indexOf(group.id) === -1) {
        // Insert at the start of the list
        groups = {
          ...state.appointmentType.groups,
          [providerId]: [
            {
              id: group.id,
              name: group.name,
              type: "group_appointment",
              is_video: group.is_video,
              provider: providerId,
              users: group.users,
              favourite_id: 0,
              phone: []
            },
            ...state.appointmentType.groups[providerId]
          ]
        };

        // Sorting the list
        groups[providerId] = groups[providerId].sort((x, y) => {
          return x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1;
        });
      }
    } else {
      groups = {
        ...state.appointmentType.groups,
        [providerId]: [
          {
            id: group.id,
            name: group.name,
            type: "group_appointment",
            is_video: group.is_video,
            provider: providerId,
            users: group.users,
            favourite_id: 0,
            phone: []
          }
        ]
      };
    }

    // Set the new selected
    Vue.set(state.appointmentType, "selectedId", group.id);
    Vue.set(state.appointmentType, "selectedType", "group_appointment");

    if (groups) {
      Vue.set(state.appointmentType, "groups", groups);
    }
  },

  setProvider(state, index) {
    if (isNaN(index)) {
      return false;
    }
    index = parseInt(index);
    Vue.set(state.provider, "selectedIndex", index);
  },
  setProviders(state, providers) {
    Vue.set(state.provider, "list", providers);
  },

  /**
   * Setting add appointment variables for edit
   *
   * @param {Object} state
   * @param {Object} param1
   */
  setEdit(state, { id, appointment }) {
    if (!isNaN(id)) {
      // Step 1: Setting start time
      state.startTime = validateAndFormatBeforeEdit.time(
        appointment["app_start_hh"],
        appointment["app_start_mm"],
        appointment["app_start_tt"]
      );

      // Step 2: Settting end time
      const endTime = validateAndFormatBeforeEdit.time(
        appointment["app_end_hh"],
        appointment["app_end_mm"],
        appointment["app_end_tt"]
      );
      Vue.set(state.endTime, "hr", endTime.hr);
      Vue.set(state.endTime, "min", endTime.min);
      Vue.set(state.endTime, "sec", endTime.sec);

      // Step 3: Setting the providerID
      const providerSelectedIndex = validateAndFormatBeforeEdit.provider(
        appointment["provider_id"],
        state
      );
      Vue.set(state.provider, "selectedIndex", providerSelectedIndex);

      // Step 4: Setting type, selected index and other event type
      const { type, selectedId } = validateAndFormatBeforeEdit.appointmentType(
        appointment
      );

      // Step 5: Setting edit details
      state.edit = {
        isEdit: true,
        editMode: state.edit.editMode,
        editLevel: state.edit.editLevel,
        firstClick: state.edit.firstClick,
        id: +id,
        usePsybooksVideo: appointment["is_video"] === 1,
        useConnectVideo: appointment["is_connect"] === 1,
        selectedType: type,
        selectedId: id
      };

      // Step 6: For "only this appointment" add the appointment to the corresponding type
      checkForOnlyThisAppointmentEdit(selectedId, type, appointment, state);
      Vue.set(state.appointmentType, "selectedType", type);
      Vue.set(state.appointmentType, "selectedId", selectedId);

      // Step 7: Setting repeat date, custom value and repeat on
      const {
        innerOrOuter,
        index,
        custom
      } = validateAndFormatBeforeEdit.repeatType(appointment);

      const weeklyOn = {
        0: false,
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false
      };

      if (appointment["repeat_on"] !== null) {
        appointment["repeat_on"].split(",").forEach(curIndex => {
          weeklyOn[curIndex] = true;
        });
      }

      Vue.set(state.repeat, "innerOrOuter", innerOrOuter);
      Vue.set(state.repeat, "selectedIndex", index);
      Vue.set(state.repeat, "custom", custom);
      Vue.set(state.repeat, "weeklyOn", weeklyOn);
      Vue.set(state.repeat, "fullMonthlyType", "");

      // Step 8: Setting repeat dates
      if (
        appointment["starts_from"] !== null &&
        appointment["end_on"] !== null
      ) {
        const end = validateAndFormatBeforeEdit.date(appointment["end_on"]);
        const start = validateAndFormatBeforeEdit.date(
          appointment["starts_from"]
        );
        state.endRepeat = end;
        state.startRepeat = {
          dayOfMonth: start.dayOfMonth,
          month: start.month,
          year: start.year
        };
      }

      // Step 9: Setting notes
      if (appointment.notes !== null) {
        state.notes = appointment.notes;
      }

      // Step 10: Setting is video
      state.allDay = isNaN(appointment["is_all_day"])
        ? false
        : parseInt(appointment["is_all_day"]) === 1;

      // Step 11: Setting is video
      state.isVideo = isNaN(appointment["type"])
        ? false
        : parseInt(appointment["type"]) === 2;

      // Step 12 Setting if it is a psybooks video appointment
      state.usePsybooksVideo = appointment["is_video"] === 1;

      state.useConnectVideo = appointment["is_connect"] === 1;

      // Step 13: Setting is office
      state.isOffice = isNaN(appointment["type"])
        ? false
        : parseInt(appointment["type"]) === 1;

      // Step 14: Setting is phone
      state.isPhone = isNaN(appointment["type"])
        ? false
        : parseInt(appointment["type"]) === 3;

      // Step 15: Check if state is other
      state.isOther = isNaN(appointment["type"])
        ? false
        : parseInt(appointment["type"]) === 4;

      state.videoReminder = appointment.video_invitepref ? appointment.video_invitepref : 'N';
    }
  },

  /**
   * Set the increment time calendar settings
   *
   * @param {Object} state
   * @param {Number} time
   */
  setAppointmentTimeIncrement(state, time = false) {
    time = isNaN(time) ? false : parseInt(time);
    if (time) {
      const hr = parseInt(time / 60);
      const min = time - hr * 60;
      const sec = 0;

      state.endTime = {
        hr:
          state.startTime.hr + hr + parseInt((state.startTime.min + min) / 60) >
          23
            ? 23
            : state.startTime.hr +
              hr +
              parseInt((state.startTime.min + min) / 60),
        min: (state.startTime.min + min) % 60,
        sec: state.startTime.sec + sec,
        incrementBy: { hr, min, sec }
      };
    } else if (!state.edit.isEdit) {
      state.endTime = {
        hr:
          state.startTime.hr +
            state.endTime.incrementBy.hr +
            parseInt(
              (state.startTime.min + state.endTime.incrementBy.min) / 60
            ) >
          23
            ? 23
            : state.startTime.hr +
              state.endTime.incrementBy.hr +
              parseInt(
                (state.startTime.min + state.endTime.incrementBy.min) / 60
              ),
        min: (state.startTime.min + state.endTime.incrementBy.min) % 60,
        sec: state.startTime.sec + state.endTime.incrementBy.sec,
        incrementBy: {
          hr: state.endTime.incrementBy.hr,
          min: state.endTime.incrementBy.min,
          sec: state.endTime.incrementBy.sec
        }
      };
    }
  },

  /**
   * Append phone numbers to the array of phone numbers of an user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  appendPhone(state, { number, selectedProviderId, clientId }) {
    // Step 1: Update for clients
    const index = state.appointmentType.clients[selectedProviderId].findIndex(
      client => {
        return client.id === clientId;
      }
    );
    if (index === -1) {
      return;
    }
    const newClients = [...state.appointmentType.clients[selectedProviderId]];
    newClients[index].phone = [number, ...newClients[index].phone];
    Vue.set(state.appointmentType, "clients", {
      ...state.appointmentType.clients,
      [selectedProviderId]: newClients
    });

    // Step 2: Update for groups
    const groups = state.appointmentType.groups[selectedProviderId].map(
      group => {
        return {
          ...group,
          users: group.users.map(user => {
            if (+user.id !== +clientId || user.type !== "person") {
              return user;
            }
            return {
              ...user,
              phone: [...(user.phone || []), number]
            };
          })
        };
      }
    );
    Vue.set(state.appointmentType, "groups", {
      ...state.appointmentType.groups,
      [selectedProviderId]: groups
    });
  },

  /**
   * Append phone for others
   *
   * @param {Object} state
   * @param {Object} param1
   */
  appendPhoneOthers(state, { number, selectedProviderId, otherEventId }) {
    // Step 2: Get the index
    const index = state.appointmentType.people[selectedProviderId].findIndex(
      person => {
        return person.id === otherEventId;
      }
    );
    if (index === -1) {
      return;
    }

    // Step 3: Create new people
    const newPeople = [...state.appointmentType.people[selectedProviderId]];
    newPeople[index].phone = [number, ...newPeople[index].phone];

    // Step 4: Set the data
    Vue.set(state.appointmentType, "people", {
      ...state.appointmentType.people,
      [selectedProviderId]: newPeople
    });

    // Step 5: Set group details
    const groups = (state.appointmentType.groups[selectedProviderId] || []).map(
      group => {
        return {
          ...group,
          users: group.users.map(user => {
            if (+user.id !== +otherEventId || user.type !== "other_event") {
              return user;
            }
            return {
              ...user,
              phone: [...(user.phone || []), number]
            };
          })
        };
      }
    );
    Vue.set(state.appointmentType, "groups", {
      ...state.appointmentType.groups,
      [selectedProviderId]: groups
    });
  },

  /**
   * Update client on update
   * When clients are edited
   *
   * @param {Object} state
   * @param {Object} param1
   */
  updateNewClient(state, { selectedProviderId, clientId, value }) {
    // Step 1: Check if data has been fetched
    if (
      selectedProviderId in state.appointmentType.clients &&
      state.appointmentType.clients[selectedProviderId].length > 0
    ) {
      // Step 2: Get the index of the client
      const index = state.appointmentType.clients[selectedProviderId].findIndex(
        client => {
          return client.id === clientId;
        }
      );
      if (index === -1) {
        return;
      }

      // Step 3: Set the clients
      const newClients = [...state.appointmentType.clients[selectedProviderId]];
      newClients[index] = { ...newClients[index], ...value };
      Vue.set(state.appointmentType, "clients", {
        ...state.appointmentType.clients,
        [selectedProviderId]: newClients
      });
    }
  },

  /**
   * Add or remove the favourite id of an user
   *
   * @param {Object} state
   * @param {Object} param1
   */
  addOrRemoveFavourites(state, { user, res, addOrRemove }) {
    // Step 1: Data sanitization
    const providerId = isNaN(user.provider) ? false : parseInt(user.provider);
    if (!providerId) {
      return false;
    }

    // Step 2: Check which list to update
    let list = false;
    switch (user.type) {
      case "other_event":
        list = "people";
        break;

      case "person":
        list = "clients";
        break;

      case "subscriber":
        list = "subscribers";
        break;

      case "app_user":
        list = "appUsers";
        break;

      case "group_appointment":
        list = "groups";
        break;

      default:
        return false;
    }

    if (user.type === "subscriber") {
      // Step 3: Get the user list
      let userList = [...state.appointmentType[list]];

      // Step 4: Update the list
      userList = userList.map(curUser => {
        if (curUser.id === user.id) {
          if (addOrRemove === "ADD") {
            curUser.favourite_id = res.favourite.id;
          } else {
            curUser.favourite_id = 0;
          }
        }
        return curUser;
      });

      // Step 5: Set the data
      Vue.set(state.appointmentType, list, userList);
    } else {
      // Step 3: Check if provider id is there
      const userList = { ...state.appointmentType[list] };
      if (!(providerId in userList)) {
        return false;
      }

      // Step 4: Update the list
      userList[providerId] = userList[providerId].map(curUser => {
        if (curUser.id === user.id) {
          if (addOrRemove === "ADD") {
            curUser.favourite_id = res.favourite.id;
          } else {
            curUser.favourite_id = 0;
          }
        }
        return curUser;
      });

      // Step 5: Set the data
      Vue.set(state.appointmentType, list, userList);
    }
  },
  addSelectedPhone(state, user) {
    // Step 1: Data sanitization
    const providerId = isNaN(user.provider) ? false : parseInt(user.provider);
    if (!providerId) {
      return false;
    }

    // Step 2: Check which list to update
    let list = false;
    switch (user.type) {
      case "other_event":
        list = "people";
        break;

      case "person":
        list = "clients";
        break;

      case "subscriber":
        list = "subscribers";
        break;

      case "app_user":
        list = "appUsers";
        break;

      default:
        return false;
    }

    if (user.type === "subscriber") {
      // Step 3: Get the user list
      let userList = [...state.appointmentType[list]];

      // Step 4: Update the list
      userList = userList.map(curUser => {
        if (curUser.id === user.id) {
          curUser.selected_phone = user.phone;
        }
        return curUser;
      });

      // Step 5: Set the data
      Vue.set(state.appointmentType, list, userList);
    } else {
      // Step 3: Check if provider id is there
      const userList = { ...state.appointmentType[list] };
      if (!(providerId in userList)) {
        return false;
      }

      // Step 4: Update the list
      userList[providerId] = userList[providerId].map(curUser => {
        if (curUser.id === user.id) {
          curUser.selected_phone = user.phone;
        }
        return curUser;
      });

      // Step 5: Set the data
      Vue.set(state.appointmentType, list, userList);
    }
  },
  /**
   * Restoring everythong to default as soon as we exit the page
   *
   * @param {Object} state
   */
  restoreDefault(state) {
    restoreDefaultForAddAppointment(state);
  },

  /**
   * On logout remove the details
   * @param {Object} state
   */
  setAddAppointmentStateToEmpty(state) {
    restoreDefaultForAddAppointment(state);
    state.appointmentType = {
      type: "",
      fetched: false,
      clients: {},
      people: {},
      events: {},
      groups: {},
      appUsers: {},
      subscribers: [],
      selectedIndex: -1,
      otherType: "",
      removeOnRefresh: {
        type: "",
        id: -1
      }
    };
    state.officeLocation = {
      custom: false,
      customValue: "",
      locationDetails: {},
      selectedIndex: 0
    };
    state.provider = {
      selectedIndex: 0,
      list: []
    };
  },
  /**
   * Emptying the appointment type edit
   *
   * @param {Object} state
   */
  emptyAppointmentTypeEdit(state) {
    state.edit = {
      isEdit: false,
      editMode: false,
      editLevel: 0,
      firstClick: false,
      id: 0,
      oneAllFuture: "single",
      type: "",
      selectedIndex: -1,
      otherType: "",
      usePsybooksVideo: false,
      useConnectVideo: false
    };
  },
  removeTemporaryEvent(state) {
    removeOnRefreshEventAction(state);
  }
};
